import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ApiService } from '@services/api/api.service';
import { ReduxService } from '@state-management/redux.service';

interface FieldOption {
  label: string;
  value: string;
}

/** A field the user selected a value for, ready to send back to the assistant. */
export interface ProvidedField {
  field: string;
  label: string;
  value: string;
}

/**
 * Lead-tag slugs that back each required field. Options come from the same source the lead form
 * uses (`redux.getTags(..., 'lead') → data[0].types[]`), so the values always match the CRM.
 */
const FIELD_SLUG: Record<string, string> = {
  lead_status: 'lead-status',
  lead_source: 'lead-source',
  enquiry_type: 'enquiry-type',
  lead_type: 'lead-type',
  lead_interest: 'lead-interest',
  temperature: 'temperature',
  property_type: 'property-type',
};

/**
 * When the assistant blocks a lead create on missing required fields, this renders a proper
 * dropdown per field (populated from the office's lead tags) so the user can pick valid values
 * inline instead of re-typing them. On submit it emits the chosen values; the panel relays them
 * back to the assistant to finish the create.
 */
@Component({
  selector: 'app-ai-assistant-required-fields',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './ai-assistant-required-fields.component.html',
  styleUrls: ['./ai-assistant-required-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantRequiredFieldsComponent implements OnInit {
  @Input() fields: string[] = [];
  @Output() provide = new EventEmitter<ProvidedField[]>();

  options: Record<string, FieldOption[]> = {};
  selected: Record<string, string> = {};
  loading = true;
  submitted = false;

  constructor(
    private readonly redux: ReduxService,
    private readonly api: ApiService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Cached fetch (force = false) — the lead tags are normally already loaded for the office.
    this.redux.getTags(this.api.getOfficeId(), false, 'lead').subscribe({
      next: res => {
        const types: any[] = (res as any)?.data?.[0]?.types || [];
        for (const field of this.fields) {
          const slug = FIELD_SLUG[field];
          if (!slug) {
            continue;
          }
          const type = types.find(t => (t?.slug || '').toLowerCase() === slug);
          this.options[field] = ((type?.data as any[]) || [])
            .slice()
            .sort((a, b) => Number(a?.tag_order) - Number(b?.tag_order))
            .map(d => ({ label: d?.tag_name, value: d?.tag_name }))
            .filter(opt => opt.label);
        }
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** A field has a real dropdown when we resolved tag options for it; otherwise it's free text. */
  isDropdown(field: string): boolean {
    return (this.options[field]?.length || 0) > 0;
  }

  /** Friendly label for a field key, e.g. `enquiry_type` → `Enquiry type`. */
  label(field: string): string {
    const text = (field || '').replace(/_/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  get canSubmit(): boolean {
    return !this.submitted && this.fields.length > 0 && this.fields.every(f => (this.selected[f] || '').trim() !== '');
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.submitted = true;
    this.provide.emit(this.fields.map(field => ({ field, label: this.label(field), value: this.selected[field] })));
  }
}
